import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2, Power, PowerOff, Calendar, User, CheckCircle, FileText } from 'lucide-react';
import { QRCode, QRCodeRedemption } from '../../../services/qrCodeManagementService';

interface PermanentQRCodeTableProps {
  qrCodes: QRCode[];
  onRefresh: () => void;
  onDeactivate: (id: string) => Promise<void>;
  onReactivate: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onLoadRedemptions: (id: string, limit: number, offset: number) => Promise<{ redemptions: QRCodeRedemption[]; total: number }>;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const PermanentQRCodeTable: React.FC<PermanentQRCodeTableProps> = ({
  qrCodes,
  onRefresh,
  onDeactivate,
  onReactivate,
  onDelete,
  onLoadRedemptions,
  itemsPerPage,
  currentPage,
  onPageChange
}) => {
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [redemptionsData, setRedemptionsData] = useState<{ [key: string]: { redemptions: QRCodeRedemption[]; total: number } }>({});
  const [loadingRedemptions, setLoadingRedemptions] = useState<Set<string>>(new Set());

  const toggleExpand = async (qrCodeId: string) => {
    const newExpanded = new Set(expandedCodes);
    
    if (newExpanded.has(qrCodeId)) {
      newExpanded.delete(qrCodeId);
    } else {
      newExpanded.add(qrCodeId);
      
      // Load redemptions if not already loaded
      if (!redemptionsData[qrCodeId]) {
        setLoadingRedemptions(prev => new Set(prev).add(qrCodeId));
        try {
          const data = await onLoadRedemptions(qrCodeId, 50, 0);
          setRedemptionsData(prev => ({ ...prev, [qrCodeId]: data }));
        } catch (error) {
          console.error('Error loading redemptions:', error);
        } finally {
          setLoadingRedemptions(prev => {
            const next = new Set(prev);
            next.delete(qrCodeId);
            return next;
          });
        }
      }
    }
    
    setExpandedCodes(newExpanded);
  };

  const handleDeactivate = async (id: string) => {
    if (window.confirm('Are you sure you want to deactivate this permanent QR code? It can be reactivated later.')) {
      await onDeactivate(id);
      onRefresh();
    }
  };

  const handleReactivate = async (id: string) => {
    await onReactivate(id);
    onRefresh();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to permanently delete this QR code? This action cannot be undone and will delete all redemption history.')) {
      await onDelete(id);
      onRefresh();
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' }
    };
    
    const badge = badges[status as keyof typeof badges] || badges.inactive;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCodes = qrCodes.slice(startIndex, endIndex);
  const totalPages = Math.ceil(qrCodes.length / itemsPerPage);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                QR Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Note
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Redemptions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedCodes.map((qr) => (
              <React.Fragment key={qr.id}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm font-mono font-medium text-gray-900">
                        {qr.code}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-900">
                        {qr.note || 'No note provided'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(qr.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-500">{formatDate(qr.createdAt)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-blue-600">{qr.totalRedemptions || 0}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleExpand(qr.id)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="View redemption history"
                      >
                        {expandedCodes.has(qr.id) ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </button>
                      
                      {qr.status === 'active' ? (
                        <button
                          onClick={() => handleDeactivate(qr.id)}
                          className="text-orange-600 hover:text-orange-800 transition-colors"
                          title="Deactivate"
                        >
                          <PowerOff className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(qr.id)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Reactivate"
                        >
                          <Power className="h-5 w-5" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDelete(qr.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                
                {/* Expanded redemption history */}
                {expandedCodes.has(qr.id) && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 bg-gray-50">
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-900">Redemption History</h4>
                        
                        {loadingRedemptions.has(qr.id) ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-sm text-gray-600">Loading redemptions...</p>
                          </div>
                        ) : redemptionsData[qr.id]?.redemptions.length > 0 ? (
                          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Redeemed At</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {redemptionsData[qr.id].redemptions.map((redemption) => (
                                  <tr key={redemption.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">
                                      <div className="flex items-center">
                                        <User className="h-4 w-4 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-900">{redemption.userName || 'Unknown'}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{redemption.userEmail}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{formatDate(redemption.redeemedAt)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 py-4 text-center">No redemptions yet</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(endIndex, qrCodes.length)} of {qrCodes.length} codes
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 border rounded-md text-sm font-medium ${
                  page === currentPage
                    ? 'bg-blue-600 text-white border-blue-600' :'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {qrCodes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No permanent QR codes found.</p>
        </div>
      )}
    </div>
  );
};

export default PermanentQRCodeTable;